import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Activity,
  Category,
  Post,
  PostInput,
  PostStatus,
  PostSummary,
  Profile,
  ReviewAction,
  Ticket,
  TicketAttachment,
  TicketContext,
  TicketInput,
} from '../types/content';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/** True when the app has been pointed at a deployed backend. */
export const isApiConfigured = Boolean(API_URL);

async function authHeader(): Promise<Record<string, string>> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new ApiError(401, 'Your session expired. Please sign in again.');
  return { Authorization: `Bearer ${token}` };
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (!API_URL) {
    throw new ApiError(
      0,
      'REACT_APP_API_URL is not set. Deploy the backend and add the API URL to your .env file.'
    );
  }

  const { method = 'GET', body, auth = false } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) Object.assign(headers, await authHeader());

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // Any write can change what a public read returns, so drop the lot.
  if (method !== 'GET') invalidateCache();

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? safeJsonParse(text) : {};

  if (!response.ok) {
    const message =
      (payload as { error?: string; message?: string }).error ||
      (payload as { message?: string }).message ||
      `Request failed with status ${response.status}`;
    throw new ApiError(response.status, message);
  }

  return payload as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

// --- Cache for the anonymous reads ------------------------------------------

/*
 * Three problems this solves, in order of how much they were felt:
 *
 * 1. The navbar and the index both asked for the sections on every page load —
 *    two identical requests racing each other for the same answer.
 * 2. Returning to the home page refetched a list that had not changed, so
 *    every navigation cost a live round trip.
 * 3. A reload had nothing to paint until the network answered, which is what
 *    put the "Loading articles…" flash on screen before the real page.
 *
 * Only anonymous GETs live here. Nothing behind `auth: true` is ever stored —
 * a cached signed-in response outliving the session is a data leak, and
 * sessionStorage is shared by everything on the origin.
 */

interface CacheEntry<T> {
  value: T;
  at: number;
}

const memo = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

/** Bumped when a cached payload's shape changes, so old tabs re-fetch. */
const CACHE_PREFIX = 'qed:v1:';

function readStored<T>(key: string): CacheEntry<T> | null {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key);
    return raw ? (JSON.parse(raw) as CacheEntry<T>) : null;
  } catch {
    // Private browsing, a full quota, or a payload written by an older build.
    return null;
  }
}

/**
 * A cached value, if one is fresh enough to paint straight away — otherwise
 * null, and the caller shows its loading state as before. Synchronous on
 * purpose: it has to be readable during the first render to be any use.
 */
export function peekCached<T>(key: string, maxAgeMs: number): T | null {
  const entry = (memo.get(key) as CacheEntry<T> | undefined) ?? readStored<T>(key);
  if (!entry || Date.now() - entry.at > maxAgeMs) return null;
  memo.set(key, entry);
  return entry.value;
}

function cachedRead<T>(key: string, load: () => Promise<T>): Promise<T> {
  const pending = inFlight.get(key) as Promise<T> | undefined;
  if (pending) return pending;

  const promise = load()
    .then((value) => {
      const entry: CacheEntry<unknown> = { value, at: Date.now() };
      memo.set(key, entry);
      try {
        sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
      } catch {
        /* Storage full or blocked; the in-memory copy still works. */
      }
      return value;
    })
    .finally(() => inFlight.delete(key));

  inFlight.set(key, promise);
  return promise;
}

/**
 * Called on every write, so a stale read can never outlive the change that
 * invalidated it — publish an edit and the article page shows the new text
 * rather than the copy cached a moment earlier. Hooked into `request` rather
 * than each mutation because the one that gets forgotten is the one that bites.
 */
function invalidateCache() {
  memo.clear();
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(CACHE_PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* Nothing to clear if storage was never available. */
  }
}

// --- Public ----------------------------------------------------------------

export const listPosts = (params: { category?: string; tag?: string } = {}) => {
  const query = new URLSearchParams();
  if (params.category) query.set('category', params.category);
  if (params.tag) query.set('tag', params.tag);
  const suffix = query.toString() ? `?${query}` : '';
  return cachedRead(`posts${suffix}`, () =>
    request<{ posts: PostSummary[] }>(`/posts${suffix}`).then((r) => r.posts)
  );
};

export const getPost = (slug: string) =>
  cachedRead(`post:${slug}`, () =>
    request<{ post: Post }>(`/posts/${encodeURIComponent(slug)}`).then((r) => r.post)
  );

/**
 * Publishing frequency per day, for the contribution heatmap. Public.
 *
 * The browser's own zone goes with the request so the server buckets posts by
 * the reader's calendar. Without it the grouping is UTC, and east of Greenwich
 * anything published after local midnight lands on the previous square — the
 * current day simply never lights up.
 */
export const getActivity = (days = 365) => {
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    /* Ancient browser: the server falls back to UTC, as it did before. */
  }
  const query = `days=${days}${tz ? `&tz=${encodeURIComponent(tz)}` : ''}`;
  // The zone is part of the cache key, or a traveller keeps yesterday's grid.
  return cachedRead(`activity:${days}:${tz}`, () => request<Activity>(`/activity?${query}`));
};

export const listCategories = () =>
  cachedRead('categories', () =>
    request<{ categories: Category[] }>('/categories').then((r) => r.categories)
  );

// --- Signed in -------------------------------------------------------------

export const getProfile = () => request<Profile>('/me', { auth: true });

/**
 * Mirrors profile changes into MongoDB. Cognito is updated first by the caller
 * — that is what sends the verification code for a new email or phone number.
 */
export const updateProfile = (input: { name?: string; email?: string; phone?: string }) =>
  request<Profile>('/me', { method: 'PUT', body: input, auth: true });

// --- Admin -----------------------------------------------------------------

// --- Your own posts (any signed-in user) ------------------------------------

export const myListPosts = () =>
  request<{ posts: PostSummary[] }>('/my/posts', { auth: true }).then((r) => r.posts);

export const myGetPost = (id: string) =>
  request<{ post: Post }>(`/my/posts/${id}`, { auth: true }).then((r) => r.post);

export const myCreatePost = (input: PostInput) =>
  request<{ post: Post }>('/my/posts', { method: 'POST', body: input, auth: true }).then(
    (r) => r.post
  );

export const myUpdatePost = (id: string, input: Partial<PostInput>) =>
  request<{ post: Post }>(`/my/posts/${id}`, { method: 'PUT', body: input, auth: true }).then(
    (r) => r.post
  );

export const myDeletePost = (id: string) =>
  request<void>(`/my/posts/${id}`, { method: 'DELETE', auth: true });

// --- Admin ------------------------------------------------------------------

export const adminListPosts = (status?: PostStatus) =>
  request<{ posts: PostSummary[] }>(
    status ? `/admin/posts?status=${status}` : '/admin/posts',
    { auth: true }
  ).then((r) => r.posts);

/** approve / reject / deactivate / reactivate. `note` is required to reject. */
export const adminReviewPost = (id: string, action: ReviewAction, note?: string) =>
  request<{ post: Post }>(`/admin/posts/${id}/review`, {
    method: 'POST',
    body: { action, note },
    auth: true,
  }).then((r) => r.post);

export const adminGetPost = (id: string) =>
  request<{ post: Post }>(`/admin/posts/${id}`, { auth: true }).then((r) => r.post);

export const adminCreatePost = (input: PostInput) =>
  request<{ post: Post }>('/admin/posts', { method: 'POST', body: input, auth: true }).then(
    (r) => r.post
  );

export const adminUpdatePost = (id: string, input: Partial<PostInput>) =>
  request<{ post: Post }>(`/admin/posts/${id}`, { method: 'PUT', body: input, auth: true }).then(
    (r) => r.post
  );

export const adminDeletePost = (id: string) =>
  request<void>(`/admin/posts/${id}`, { method: 'DELETE', auth: true });

export const adminListCategories = () =>
  request<{ categories: Category[] }>('/admin/categories', { auth: true }).then(
    (r) => r.categories
  );

export const adminCreateCategory = (input: { name: string; description?: string }) =>
  request<{ category: Category }>('/admin/categories', {
    method: 'POST',
    body: input,
    auth: true,
  }).then((r) => r.category);

export const adminUpdateCategory = (
  id: string,
  input: { name?: string; description?: string; order?: number; slug?: string }
) =>
  request<{ category: Category }>(`/admin/categories/${id}`, {
    method: 'PUT',
    body: input,
    auth: true,
  }).then((r) => r.category);

export const adminDeleteCategory = (id: string) =>
  request<void>(`/admin/categories/${id}`, { method: 'DELETE', auth: true });

// --- Media -----------------------------------------------------------------

interface UploadTicket {
  uploadUrl: string;
  publicUrl: string;
  key: string;
  contentType: string;
}

/**
 * Two-step upload: ask the API for a presigned URL, then PUT the bytes straight
 * to S3 so image data never travels through Lambda.
 */
export async function uploadImage(file: File, postId?: string): Promise<string> {
  const ticket = await request<UploadTicket>('/admin/media/upload-url', {
    method: 'POST',
    auth: true,
    body: {
      filename: file.name,
      contentType: file.type,
      size: file.size,
      // Groups the object under media/<author>/<post>/ so deleting the post
      // can remove its images in one prefix sweep.
      postId,
    },
  });

  const upload = await fetch(ticket.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': ticket.contentType,
      'Cache-Control': 'public, max-age=31536000, immutable',
    },
    body: file,
  });

  if (!upload.ok) {
    throw new ApiError(upload.status, 'Upload to storage failed. Please try again.');
  }

  return ticket.publicUrl;
}

// --- Feedback tickets -------------------------------------------------------

/**
 * Everything the browser knows that the reporter cannot describe.
 *
 * `buildCommit` is the one that earns its place: it answers "are they even
 * running the fixed code?" before anything else is investigated. A whole
 * debugging session has already been spent on a device holding a stale bundle.
 */
export function captureContext(url?: string): TicketContext {
  return {
    url: url || window.location.pathname + window.location.search,
    viewport: `${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio || 1}x`,
    userAgent: navigator.userAgent,
    buildCommit: process.env.REACT_APP_COMMIT || 'unknown',
    errors: recentErrors(),
  };
}

/*
 * A tiny ring buffer of console errors, installed once on import.
 *
 * Capped hard at five entries and 500 characters each. The server truncates to
 * the same limits, so this is about not shipping a megabyte of stack traces
 * rather than about trusting the client.
 */
const ERROR_LOG: string[] = [];
const recentErrors = () => ERROR_LOG.slice(-5);

if (typeof window !== 'undefined') {
  const original = console.error;
  console.error = (...args: unknown[]) => {
    try {
      ERROR_LOG.push(args.map((a) => String(a)).join(' ').slice(0, 500));
      if (ERROR_LOG.length > 5) ERROR_LOG.shift();
    } catch {
      /* Never let logging break the page. */
    }
    original(...args);
  };
  window.addEventListener('error', (e) => {
    ERROR_LOG.push(`${e.message} @ ${e.filename}:${e.lineno}`.slice(0, 500));
    if (ERROR_LOG.length > 5) ERROR_LOG.shift();
  });
}

export const myCreateTicket = (input: TicketInput) =>
  request<{ ticket: Ticket }>('/my/tickets', { method: 'POST', body: input, auth: true }).then(
    (r) => r.ticket
  );

export const myListTickets = () =>
  request<{ tickets: Ticket[] }>('/my/tickets', { auth: true }).then((r) => r.tickets);

export const myGetTicket = (id: string) =>
  request<{ ticket: Ticket }>(`/my/tickets/${id}`, { auth: true }).then((r) => r.ticket);

export const myCommentOnTicket = (id: string, body: string) =>
  request<{ ticket: Ticket }>(`/my/tickets/${id}/comments`, {
    method: 'POST',
    body: { body },
    auth: true,
  }).then((r) => r.ticket);

export const adminListTickets = (status?: string) =>
  request<{ tickets: Ticket[] }>(
    status ? `/admin/tickets?status=${status}` : '/admin/tickets',
    { auth: true }
  ).then((r) => r.tickets);

export const adminGetTicket = (id: string) =>
  request<{ ticket: Ticket }>(`/admin/tickets/${id}`, { auth: true }).then((r) => r.ticket);

export const adminUpdateTicket = (
  id: string,
  input: { status?: string; severity?: string; type?: string; adminNote?: string }
) =>
  request<{ ticket: Ticket }>(`/admin/tickets/${id}`, {
    method: 'PUT',
    body: input,
    auth: true,
  }).then((r) => r.ticket);

export const adminCommentOnTicket = (id: string, body: string) =>
  request<{ ticket: Ticket }>(`/admin/tickets/${id}/comments`, {
    method: 'POST',
    body: { body },
    auth: true,
  }).then((r) => r.ticket);

/**
 * Two-step, like image upload: ask for a presigned URL, then PUT the bytes
 * straight to S3 so a 50 MB screen recording never travels through Lambda.
 */
export async function uploadTicketAttachment(file: File): Promise<TicketAttachment> {
  const ticket = await request<{ uploadUrl: string; key: string; contentType: string }>(
    '/my/tickets/upload-url',
    {
      method: 'POST',
      auth: true,
      body: { filename: file.name, contentType: file.type, size: file.size },
    }
  );

  const upload = await fetch(ticket.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': ticket.contentType },
    body: file,
  });

  if (!upload.ok) {
    throw new ApiError(upload.status, 'Upload failed. Please try again.');
  }

  return {
    key: ticket.key,
    contentType: ticket.contentType,
    size: file.size,
    originalName: file.name,
  };
}

// --- AI question answering --------------------------------------------------

export interface AiQuota {
  used: number;
  limit: number;
  remaining: number;
  unlimited: boolean;
}

export interface AiAnswer {
  answer: string;
  cached: boolean;
  truncated?: boolean;
  quota: AiQuota;
}

/**
 * Asks about one published article.
 *
 * The provider key is never here — the request goes to our own API, which
 * holds the key and calls the model. A key in this bundle would be public the
 * moment it deployed, and billed to whoever found it.
 */
export const askAboutPost = (postId: string, question: string) =>
  request<AiAnswer>('/my/ask', { method: 'POST', body: { postId, question }, auth: true });

/** Today's remaining allowance, so the UI can say so before anyone types. */
export const getAiQuota = () =>
  request<{ quota: AiQuota }>('/my/ask', { auth: true }).then((r) => r.quota);
